import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow,
    TableCell, TextField, Chip, Button, CircularProgress, Paper, Alert
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { ITaxExemption } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';

const TaxExemptionList: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [exemptions, setExemptions] = useState<ITaxExemption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        TaxService.getExemptions(institutionId)
            .then(setExemptions)
            .catch(() => showError('Failed to load exemptions'))
            .finally(() => setLoading(false));
    }, [institutionId]);

    const updateCap = (idx: number, value: string) => {
        setExemptions(prev => prev.map((e, i) =>
            i === idx ? { ...e, monthlyExemptionCap: parseFloat(value) || 0 } : e));
    };

    const save = async () => {
        setSaving(true);
        try {
            const saved = await TaxService.saveExemptions(exemptions, institutionId);
            setExemptions(saved);
            showSuccess('Exemptions saved');
        } catch {
            showError('Failed to save exemptions');
        } finally { setSaving(false); }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box data-help-page="tax.exemptions.page" p={3}>
            <Typography variant="h5" mb={1}>Component Exemptions</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Set the maximum monthly amount of each earning component that is exempt from tax. Set 0 to make it fully taxable.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                Only EARNING components are shown. DEDUCTION components are never part of taxable income.
            </Alert>

            <Paper>
                <ScrollableTable>
                    <Table data-help-section="tax.exemptions.table" size="small">
                        <TableHead>
                            <TableRow sx={{ background: '#f1f5f9' }}>
                                <TableCell data-help="tax.exemptions.col.component">Component</TableCell>
                                <TableCell data-help="tax.exemptions.col.type">Type</TableCell>
                                <TableCell data-help="tax.exemptions.col.cap">Exemption Cap / Month</TableCell>
                                <TableCell data-help="tax.exemptions.col.status">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {exemptions.map((ex, idx) => (
                                <TableRow key={ex.salaryComponentId}>
                                    <TableCell>{ex.componentName}</TableCell>
                                    <TableCell>
                                        <Chip label="EARNING" color="success" size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <TextField data-help="tax.exemptions.action.cap"
                                            size="small" type="number"
                                            value={ex.monthlyExemptionCap}
                                            onChange={e => updateCap(idx, e.target.value)}
                                            inputProps={{ min: 0 }}
                                            sx={{ width: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {ex.monthlyExemptionCap > 0
                                            ? <Chip label="Partially Exempt" color="success" size="small" />
                                            : <Chip label="Fully Taxable" color="error" size="small" />}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ScrollableTable>
                <Box p={2}>
                    <Button data-help="tax.exemptions.action.save" variant="contained" onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Exemptions'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default TaxExemptionList;
